import { useState } from "react";
import { Menu, X, ClipboardList, ChevronDown } from "lucide-react";
import { Link } from "@tanstack/react-router";
import logo from "@/assets/logo-header.webp";

const links = [
  { label: "Privatlån", to: "/privatlan" },
  { label: "Billån", to: "/billan" },
  { label: "Samla lån", to: "/samla-lan" },
  { label: "Snabblån", to: "/snabblan" },
  { label: "SMS-lån", to: "/smslan" },
  { label: "Lån utan UC", to: "/lan-utan-uc" },
] as const;

const infoLinks = [
  { label: "Kontakt", to: "/kontakt" },
  { label: "Lånekalkyl", to: "/guider/lanekalkyl" },
  { label: "Nyheter", to: "/nyhet" },
] as const;

export function Header() {
  const [open, setOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center">
          <img
            src={logo}
            alt="Svensk Låneguide"
            width={290}
            height={100}
            decoding="async"
            className="h-9 w-auto sm:h-10"
          />
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              activeOptions={{ exact: true }}
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-primary-soft hover:text-primary [&.active]:bg-primary-soft [&.active]:text-primary"
            >
              {l.label}
            </Link>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setInfoOpen(true)}
            onMouseLeave={() => setInfoOpen(false)}
          >
            <button
              type="button"
              onClick={() => setInfoOpen((o) => !o)}
              aria-expanded={infoOpen}
              className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-primary-soft hover:text-primary"
            >
              Information
              <ChevronDown className={`h-4 w-4 transition-transform ${infoOpen ? "rotate-180" : ""}`} />
            </button>
            {infoOpen && (
              <div className="absolute left-0 top-full w-48 pt-2">
                <div className="animate-fade-in rounded-md border border-border bg-card p-1.5 shadow-elevated">
                  {infoLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setInfoOpen(false)}
                      className="block rounded-md px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-primary-soft hover:text-primary [&.active]:bg-primary-soft [&.active]:text-primary"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/lanetest"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:brightness-110"
          >
            <ClipboardList className="h-4 w-4" /> Lånetest
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <Link
            to="/lanetest"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm"
          >
            <ClipboardList className="h-4 w-4" /> Lånetest
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            className="grid h-10 w-10 place-items-center rounded-md border border-border bg-card"
            aria-label="Meny"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-card lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-[15px] font-medium text-foreground hover:bg-primary-soft [&.active]:bg-primary-soft [&.active]:text-primary"
              >
                {l.label}
              </Link>
            ))}

            <div className="mt-2 border-t border-border pt-3">
              <div className="px-3 pb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Information
              </div>
              {infoLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-3 py-2.5 text-[15px] font-medium text-foreground hover:bg-primary-soft [&.active]:bg-primary-soft [&.active]:text-primary"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
